import React, { useEffect } from 'react';
import { Button, colors, Tooltip, useThemeContext } from '@insurancemenu/imenu-components-lib';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import clsx from 'clsx';
import { AccountCircle, Cart, Home } from '@insurancemenu/imenu-components-lib/dist/icons';
import { HeaderSponsorContentMap } from '@insurancemenu/imenu-components-lib/dist/components/HeaderCommon/sharedStaticData';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { Link, useHistory } from 'react-router-dom';
import { useQueryParams } from 'global/hooks/useQuery';
import ChoiceModal from 'components/common/modals/ChoiceModal';
import { createStyles } from './styles';
import MobileDropdown from './MobileDropdown';

const Header = observer((): JSX.Element => {
  const {
    domainStore: { cartId, showCartLink, domainHeader, cartItemsQuantity, openChoiceModal },
  } = useRootStore();

  const history = useHistory();
  const query = useQueryParams();
  const cartType = query.get('cartType') || '';
  const groupId = query.get('groupId') || '';
  const cartIdFromURL = query.get('cartId') || '';

  const { accentColor } = useThemeContext();

  // Moved this logic to the Header component inside the router to make use of hooks like useHistory and useQueryParams
  // This allows for cleaner separation of concerns and better integration with the routing system
  useEffect(() => {
    if (!groupId || !cartIdFromURL) {
      history.push('/browse-info');
    }
  }, []);

  const { headerStyleId, name: domainName } = toJS(domainHeader);
  const currentSponsorContent =
    headerStyleId && HeaderSponsorContentMap[domainHeader.headerStyleId]
      ? HeaderSponsorContentMap[domainHeader.headerStyleId]
      : HeaderSponsorContentMap.default;

  const classes = createStyles({
    customMainColor: accentColor,
    customLogoWidth: currentSponsorContent.customLogoWidth,
    showCartLink,
  });
  const cartLink = `/cart?groupId=${groupId}&cartId=${cartId}&cartType=${cartType}`;
  const isBrowseInfoPage = history.location.pathname.includes('/browse-info');

  return (
    <>
      <header className={classes.header}>
        <div className={classes.mobileHeaderWrapper}>
          <a href="browse-info" aria-label="Browse Info" className={classes.mobileHomeLink}>
            <Home width="3.2rem" height="3.2rem" />
          </a>
          <Tooltip label={domainName} alignment={AlignmentTypes.BOTTOM_CENTER}>
            <div className={classes.mobileScreenLogoWrapper}>
              <img
                src={currentSponsorContent.logoUrl}
                alt={`${domainName || 'InsuranceMenu'} logo`}
                className={classes.mobileScreenLogo}
              />
            </div>
          </Tooltip>
          <MobileDropdown
            isBrowseInfoPage={isBrowseInfoPage}
            customMainColor={accentColor || colors.neutral[900]}
            cartLink={showCartLink ? cartLink : ''}
            contactMeAction={(): void => {
              openChoiceModal('CONTACT_ME');
            }}
          />
        </div>
        <div className={classes.desktopHeaderWrapper}>
          <section className={classes.baseSection}>
            <div className={classes.headerLogoWrapper}>
              <Tooltip label={domainName} alignment={AlignmentTypes.BOTTOM_CENTER}>
                <img
                  src={currentSponsorContent.logoUrl}
                  alt={`${domainName || 'InsuranceMenu'} logo`}
                  className={classes.desktopScreenLogo}
                />
              </Tooltip>
            </div>
            <nav className={classes.navigation}>
              <ul className={classes.pagesLinksBlock} role="menu">
                <li role="menuitem">
                  <a href="/browse-info" className={clsx(classes.link, classes.pageLink)}>
                    <Home width="2.4rem" height="2.4rem" />
                    <span className={classes.pageLinkText}>Home</span>
                  </a>
                </li>
              </ul>
              {!isBrowseInfoPage && (
                <ul className={classes.additionalLinksBlock} role="menu">
                  <li className={classes.additionalLinksItem} role="menuitem">
                    <Button
                      leftIconProp={<AccountCircle />}
                      type="text"
                      size="medium"
                      className={classes.contactMeButton}
                      onClick={(): void => {
                        openChoiceModal('CONTACT_ME');
                      }}
                    >
                      Contact me
                    </Button>
                  </li>
                  {showCartLink && (
                    <li role="menuitem">
                      <Link to={cartLink} className={clsx(classes.pageLink, classes.cartLink)}>
                        <Cart width="2.4rem" height="2.4rem" />
                        <span className={classes.pageLinkText}>{`Cart (${cartItemsQuantity})`}</span>
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </nav>
          </section>
        </div>
      </header>
      <ChoiceModal />
    </>
  );
});

export default Header;
